import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { Map, Marker, PointLike, GeoJSONSource } from "mapbox-gl";
import TAAudioAgent, { RecommendationData } from "ta-agent-sdk";
import useIsMobile from "../hooks/useIsMobile";
// import { INITIAL_CENTER, INITIAL_ZOOM } from "../components/Map";

type MapsContextType = {
  agent: TAAudioAgent | null;
  setAgent: (agent: TAAudioAgent) => void;
  map: Map | null;
  markersChat: Marker[];
  setMap: (map: Map | null) => void; // allow null on unmount to avoid using removed map instance
  flyMapTo: (longitude: number, latitude: number, zoom?: number) => void;
  setMarkersChat: (markers: Marker[]) => void;
  addMarkerChat: (place: RecommendationData) => void;
  getMarkersChat: () => Marker[];
  resetMarkersChat: () => void;
  // 2D/3D & selection
  is3D: boolean;
  set3D: (val: boolean, move?: boolean) => void;
  selectedRecommendation: RecommendationData | null;
  setSelectedRecommendation: (rec: RecommendationData | null) => void;
  openDetails: (place: RecommendationData, interrupt: boolean) => void;
  closeDetails: () => void;
  currentTile: RecommendationData | null;
  setCurrentTile: (tile: RecommendationData | null) => void;
};

const MapsContext = createContext<MapsContextType | undefined>(undefined);

const imageMapping = {
  Experience: "attraction",
  Attraction: "attraction",
  "Eat & Drink": "eat_and_drink",
  Accommodation: "accommodation",
};

const clusterSourceId = "chat-places";
export const MapsProvider = ({ children }: { children: ReactNode }) => {
  const [agent, setAgent] = useState<TAAudioAgent | null>(null);
  const [map, setMap] = useState<Map | null>(null);
  const [markersChat, setMarkersChat] = useState<Marker[]>([]);
  const [is3D, setIs3D] = useState<boolean>(false);
  const [currentTile, setCurrentTile] = useState<RecommendationData | null>(
    null
  );
  const isMobile = useIsMobile();
  const [selectedRecommendation, setSelectedRecommendation] =
    useState<RecommendationData | null>(null);
  const orbitTimeoutRef = useRef<number | null>(null);
  const orbitIntervalRef = useRef<number | null>(null);
  const orbitCenterRef = useRef<[number, number] | null>(null);
  // clustering source/state
  const clusterLayersAddedRef = useRef<boolean>(false);
  const featureCollectionRef = useRef<GeoJSON.FeatureCollection<GeoJSON.Point>>(
    {
      type: "FeatureCollection",
      features: [],
    }
  );
  const placeIndexRef = useRef<Record<string, RecommendationData>>({});
  const openDetailsRef = useRef<
    (p: RecommendationData, interrupt: boolean) => void
  >(() => {});
  // Track selected feature id (string or number) for feature-state toggling
  const selectedFeatureIdRef = useRef<string | number | null>(null);

  // Helper to extract a human-friendly name for labeling
  const getPlaceName = useCallback((place: RecommendationData): string => {
    if ("location" in place) {
      return place.location.names.find((name) => name.primary)?.value || "";
    } else {
      return place.experience.names.find((name) => name.primary)?.value || "";
    }
  }, []);

  // Helper to get coordinates from a place
  const getPlaceCoords = useCallback(
    (place: RecommendationData): [number, number] | null => {
      if ("location" in place && place.location.coordinates) {
        return [
          place.location.coordinates.longitude,
          place.location.coordinates.latitude,
        ];
      } else if ("experience" in place && place.experience.coordinates) {
        return [
          place.experience.coordinates.longitude,
          place.experience.coordinates.latitude,
        ];
      }
      return null;
    },
    []
  );

  // Helper to compare two places (best-effort using coords + primary name)
  const isSamePlace = useCallback(
    (a: RecommendationData, b: RecommendationData): boolean => {
      const p1 = getPlaceCoords(a);
      const p2 = getPlaceCoords(b);
      if (!p1 || !p2) return false;
      const [alng, alat] = p1;
      const [blng, blat] = p2;
      if (alng !== blng || alat !== blat) return false;
      return getPlaceName(a) === getPlaceName(b);
    },
    [getPlaceCoords, getPlaceName]
  );

  const stopOrbit = useCallback(() => {
    if (orbitTimeoutRef.current) {
      clearTimeout(orbitTimeoutRef.current);
      orbitTimeoutRef.current = null;
    }
    if (orbitIntervalRef.current) {
      clearInterval(orbitIntervalRef.current);
      orbitIntervalRef.current = null;
    }
    orbitCenterRef.current = null;
  }, []);

  const startOrbit = useCallback(
    (longitude: number, latitude: number) => {
      stopOrbit();
      orbitCenterRef.current = [longitude, latitude];
      // wait 2s, then begin rotating
      orbitTimeoutRef.current = window.setTimeout(() => {
        if (!map) return;
        // gentle, continuous rotation
        orbitIntervalRef.current = window.setInterval(() => {
          if (!map || !orbitCenterRef.current) return;
          const nextBearing = (map.getBearing() + 4) % 360; // slower deg step
          // keep center locked to the POI for a proper orbit effect
          map.easeTo({
            center: orbitCenterRef.current,
            bearing: nextBearing,
            duration: 550,
            easing: (t) => t,
            zoom: map.getZoom(),
            pitch: map.getPitch(),
            ...(isMobile && { padding: { bottom: 300 } }),
          });
        }, 500);
      }, 2000);
    },
    [map, stopOrbit, isMobile]
  );

  // Stop orbit on any user interaction and close dialog if open
  useEffect(() => {
    if (!map) return;
    const stopIfUser = (e: any) => {
      if (e && (e as any).originalEvent) {
        stopOrbit();
        if (selectedRecommendation) {
          setSelectedRecommendation(null);
          setIs3D(false);
        }
      }
    };
    const stopImmediate = () => {
      stopOrbit();
      if (selectedRecommendation) {
        setSelectedRecommendation(null);
        setIs3D(false);
      }
    };

    map.on("movestart", stopIfUser);
    map.on("zoomstart", stopIfUser);
    map.on("rotatestart", stopIfUser);
    map.on("pitchstart", stopIfUser);
    map.on("dragstart", stopIfUser);
    map.on("mousedown", stopImmediate);
    map.on("touchstart", stopImmediate);

    return () => {
      map.off("movestart", stopIfUser);
      map.off("zoomstart", stopIfUser);
      map.off("rotatestart", stopIfUser);
      map.off("pitchstart", stopIfUser);
      map.off("dragstart", stopIfUser);
      map.off("mousedown", stopImmediate);
      map.off("touchstart", stopImmediate);
    };
  }, [map, stopOrbit, selectedRecommendation]);

  // (moved syncing effect below after openDetails is defined)

  // Setup clustering source and layers once the map is available
  useEffect(() => {
    if (!map) return;
    const srcId = clusterSourceId;

    const ensureLayers = () => {
      const cleanupFns: Array<() => void> = [];
      if (!map.getSource(srcId)) {
        map.addSource(srcId, {
          type: "geojson",
          data: featureCollectionRef,
          cluster: true,
          clusterMaxZoom: 10,
          clusterRadius: 50,
          generateId: true,
        } as any);
      }

      // Preload the icon images we may reference via feature property `image`
      const iconDefs = [
        { name: "attraction", src: "/icons/attraction.svg" },
        { name: "eat_and_drink", src: "/icons/eat_and_drink.svg" },
        { name: "accommodation", src: "/icons/accommodation.svg" },
        { name: "experience", src: "/icons/attraction.svg" },
      ] as const;

      const loadIcon = (name: string, src: string) => {
        if (map.hasImage(name)) return;
        const img = new Image(30, 30);
        img.crossOrigin = "anonymous";
        img.onload = () => {
          try {
            if (!map.hasImage(name)) {
              map.addImage(name, img, { pixelRatio: 2 });
            }
          } catch {}
        };
        img.src = src;
        // Cleanup: remove image when tearing down
        cleanupFns.push(() => {
          if (map.hasImage(name)) {
            try {
              map.removeImage(name);
            } catch {}
          }
        });
      };
      iconDefs.forEach((d) => loadIcon(d.name, d.src));

      // We'll add the symbol layer after the circle layer so icons are not hidden.
      const ensureSymbolLayer = () => {
        if (!map.getLayer("chat-unclustered")) {
          map.addLayer({
            id: "chat-unclustered",
            type: "symbol",
            source: srcId,
            filter: ["!", ["has", "point_count"]],
            layout: {
              // Use the feature property `image`; fallback to name 'experience'
              "icon-image": ["coalesce", ["get", "image"], "experience"],
              "icon-size": 1.4,
              "icon-allow-overlap": true,
              "icon-ignore-placement": true,
              "icon-anchor": "center",
              "icon-pitch-alignment": "viewport",
            },
            paint: {
              "fill-color": "#ffffff",
              "icon-color": "#ffffff",
            },
          });
          console.debug("Added unclustered symbol layer (data-driven icons)");
        }
        // Ensure ordering: circle (base) -> symbol (icon) -> label (text)
        try {
          if (map.getLayer("chat-unclustered-label")) {
            map.moveLayer("chat-unclustered", "chat-unclustered-label");
          } else if (map.getLayer("chat-unclustered-circle")) {
            // move above circle but below any future label
            map.moveLayer("chat-unclustered", "chat-unclustered-circle");
          }
        } catch {}
      };

      if (!map.getLayer("chat-clusters")) {
        map.addLayer({
          id: "chat-clusters",
          type: "circle",
          source: srcId,
          filter: ["has", "point_count"],
          paint: {
            "circle-color": "#002B11",
            "circle-radius": 20,
            "circle-stroke-color": "#ffffff",
            "circle-stroke-width": 3,
          },
        });
      }

      if (!map.getLayer("chat-cluster-count")) {
        map.addLayer({
          id: "chat-cluster-count",
          type: "symbol",
          source: srcId,
          filter: ["has", "point_count"],
          layout: {
            "text-field": ["get", "point_count_abbreviated"],
            "text-font": ["TripSansBold"],
            "text-size": 13,
          },
          paint: {
            "text-color": "#fff",
            "text-halo-width": 1.2,
          },
        });
      }

      // Base circle to ensure unclustered points are clearly visible
      if (!map.getLayer("chat-unclustered-circle")) {
        map.addLayer({
          id: "chat-unclustered-circle",
          type: "circle",
          source: srcId,
          filter: ["!", ["has", "point_count"]],
          paint: {
            // blue when selected, red otherwise
            "circle-color": [
              "case",
              ["boolean", ["feature-state", "selected"], false],
              "#294B35",
              "#002B11",
            ],
            // slightly larger when selected
            "circle-radius": [
              "case",
              ["boolean", ["feature-state", "selected"], false],
              20,
              18,
            ],
            "circle-stroke-color": "#ffffff",
            "circle-stroke-width": 3,
          },
        });
      }
      // After adding/ensuring circle layer, ensure symbol layer is correctly layered
      ensureSymbolLayer();

      // Text label next to unclustered markers
      if (!map.getLayer("chat-unclustered-label")) {
        map.addLayer({
          id: "chat-unclustered-label",
          type: "symbol",
          source: srcId,
          filter: ["!", ["has", "point_count"]],
          layout: {
            "text-field": ["coalesce", ["get", "title"], ""],
            "text-font": ["TripSansMedium", "TripSansRegular"],
            "text-size": 15,
            "text-offset": [1.6, 0],
            "text-anchor": "left",
          },
          paint: {
            "text-color": [
              "case",
              ["boolean", ["feature-state", "selected"], false],
              "#1f2937",
              "#111827",
            ],
            "text-halo-color": "#ffffff",
            "text-halo-width": 1.6,
          },
        });
        // Re-assert ordering now that label exists
        ensureSymbolLayer();
      }

      // Symbol layer already added above; icons load as they become available

      if (!clusterLayersAddedRef.current) {
        clusterLayersAddedRef.current = true;

        // Cluster click to expand
        const handleClusterClick = (e: any) => {
          const features = map.queryRenderedFeatures(e.point, {
            layers: ["chat-clusters"],
          });
          if (!features?.length) return;
          const clusterId = (features[0] as any).properties.cluster_id;
          const src = map.getSource(srcId) as GeoJSONSource;
          if (!src) return;
          src.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            if (typeof zoom === "number") {
              map.easeTo({
                center: (features[0] as any).geometry.coordinates,
                zoom,
              });
            } else {
              map.easeTo({ center: (features[0] as any).geometry.coordinates });
            }
          });
        };
        map.on("click", "chat-clusters", handleClusterClick);
        cleanupFns.push(() =>
          map.off("click", "chat-clusters", handleClusterClick)
        );

        // Unclustered click -> open details
        const handleUnclusteredClick = (e: any) => {
          const f = e.features && e.features[0];
          if (!f) return;
          const id = f.properties?.id as string | undefined;
          if (!id) return;
          const p = placeIndexRef.current[id];
          // Highlight the clicked feature via feature-state
          const featureId = f.id as number | string | undefined;
          if (typeof featureId === "number" || typeof featureId === "string") {
            if (selectedFeatureIdRef.current !== null) {
              map.setFeatureState(
                { source: srcId, id: selectedFeatureIdRef.current },
                { selected: false }
              );
            }
            selectedFeatureIdRef.current = featureId;
            map.setFeatureState(
              { source: srcId, id: featureId },
              { selected: true }
            );
          }
          if (p) openDetailsRef.current(p, false);
        };
        map.on("click", "chat-unclustered", handleUnclusteredClick);
        map.on("click", "chat-unclustered-circle", handleUnclusteredClick);
        map.on("click", "chat-unclustered-label", handleUnclusteredClick);
        cleanupFns.push(() =>
          map.off("click", "chat-unclustered", handleUnclusteredClick)
        );
        cleanupFns.push(() =>
          map.off("click", "chat-unclustered-circle", handleUnclusteredClick)
        );
        cleanupFns.push(() =>
          map.off("click", "chat-unclustered-label", handleUnclusteredClick)
        );

        // Cursor feedback
        const handleClusterEnter = () => {
          map.getCanvas().style.cursor = "pointer";
        };
        const handleClusterLeave = () => {
          map.getCanvas().style.cursor = "";
        };
        const handleUnclusteredEnter = () => {
          map.getCanvas().style.cursor = "pointer";
        };
        const handleUnclusteredLeave = () => {
          map.getCanvas().style.cursor = "";
        };
        map.on("mouseenter", "chat-clusters", handleClusterEnter);
        map.on("mouseleave", "chat-clusters", handleClusterLeave);
        map.on("mouseenter", "chat-unclustered", handleUnclusteredEnter);
        map.on("mouseleave", "chat-unclustered", handleUnclusteredLeave);
        map.on("mouseenter", "chat-unclustered-circle", handleUnclusteredEnter);
        map.on("mouseleave", "chat-unclustered-circle", handleUnclusteredLeave);
        map.on("mouseenter", "chat-unclustered-label", handleUnclusteredEnter);
        map.on("mouseleave", "chat-unclustered-label", handleUnclusteredLeave);
        cleanupFns.push(() =>
          map.off("mouseenter", "chat-clusters", handleClusterEnter)
        );
        cleanupFns.push(() =>
          map.off("mouseleave", "chat-clusters", handleClusterLeave)
        );
        cleanupFns.push(() =>
          map.off("mouseenter", "chat-unclustered", handleUnclusteredEnter)
        );
        cleanupFns.push(() =>
          map.off("mouseleave", "chat-unclustered", handleUnclusteredLeave)
        );
        cleanupFns.push(() =>
          map.off(
            "mouseenter",
            "chat-unclustered-circle",
            handleUnclusteredEnter
          )
        );
        cleanupFns.push(() =>
          map.off(
            "mouseleave",
            "chat-unclustered-circle",
            handleUnclusteredLeave
          )
        );
        cleanupFns.push(() =>
          map.off(
            "mouseenter",
            "chat-unclustered-label",
            handleUnclusteredEnter
          )
        );
        cleanupFns.push(() =>
          map.off(
            "mouseleave",
            "chat-unclustered-label",
            handleUnclusteredLeave
          )
        );
      }
      // return a cleanup that removes layers/listeners if needed
      return () => {
        cleanupFns.forEach((fn) => fn());
      };
    };

    let cleanup: (() => void) | undefined;
    if (map.loaded()) {
      cleanup = ensureLayers();
    } else {
      const onLoad = () => {
        cleanup = ensureLayers();
      };
      map.once("load", onLoad);
      return () => {
        map.off("load", onLoad);
        if (cleanup) cleanup();
      };
    }
    return () => {
      if (cleanup) cleanup();
      // Optionally remove layers and source to prevent duplicates if map re-initializes
      [
        "chat-cluster-count",
        "chat-clusters",
        "chat-unclustered",
        "chat-unclustered-circle",
        "chat-unclustered-label",
      ].forEach((id) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      if (map.getSource(srcId)) map.removeSource(srcId);
      clusterLayersAddedRef.current = false;
      selectedFeatureIdRef.current = null;
    };
  }, [map]);

  const addMarkerChat = useCallback(
    (place: RecommendationData) => {
      if (!map) return;

      let lng: number | undefined, lat: number | undefined;
      let type: "Experience" | "Attraction" | "Eat & Drink" | "Accommodation" =
        "Experience";
      if ("location" in place && place.location.coordinates) {
        lng = place.location.coordinates.longitude;
        lat = place.location.coordinates.latitude;
        type =
          place.location.categories.length > 0
            ? (place.location.categories[0].top_level_category
                .display_name as any)
            : "Attraction";
      } else if ("experience" in place && place.experience.coordinates) {
        lng = place.experience.coordinates.longitude;
        lat = place.experience.coordinates.latitude;
      }

      if (lng === undefined || lat === undefined) return;

      // Track place and add feature to the clustered source
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      placeIndexRef.current[id] = place;
      const title = getPlaceName(place);
      featureCollectionRef.current.features.push({
        type: "Feature",
        // Assign a stable feature id so we can target feature-state reliably
        id,
        geometry: { type: "Point", coordinates: [lng, lat] },
        properties: { id, title, image: imageMapping[type] || "experience" },
      } as any);

      const src = map.getSource(clusterSourceId) as GeoJSONSource | undefined;
      if (src) {
        src.setData(featureCollectionRef.current as any);
      } else {
        // If source not yet present, it will pick up via useEffect once map loads
      }
    },
    [map, getPlaceName]
  );

  // Keep feature-state `selected` in sync with selectedRecommendation
  useEffect(() => {
    if (!map) return;
    const srcId = clusterSourceId;
    const src = map.getSource(srcId) as GeoJSONSource | undefined;

    // Clear previous selection state
    if (selectedFeatureIdRef.current !== null && src) {
      try {
        map.setFeatureState(
          { source: srcId, id: selectedFeatureIdRef.current },
          { selected: false }
        );
      } catch {}
      selectedFeatureIdRef.current = null;
    }

    if (!selectedRecommendation || !src) return;

    // Find the corresponding feature id for the selected place
    let matchId: string | number | null = null;
    // First try via placeIndexRef to properties.id mapping
    for (const [pid, p] of Object.entries(placeIndexRef.current)) {
      if (isSamePlace(p, selectedRecommendation)) {
        matchId = pid;
        break;
      }
    }
    // Fallback: scan features by coords + title
    if (matchId === null) {
      const p = getPlaceCoords(selectedRecommendation);
      if (!p) return;
      const [lng, lat] = p;
      const title = getPlaceName(selectedRecommendation);
      for (const f of featureCollectionRef.current.features) {
        const coords = (f.geometry as any).coordinates as [number, number];
        const t = (f.properties as any)?.title as string | undefined;
        if (coords && coords[0] === lng && coords[1] === lat && t === title) {
          matchId = (f as any).id ?? (f.properties as any)?.id ?? null;
          break;
        }
      }
    }

    if (matchId !== null) {
      try {
        map.setFeatureState({ source: srcId, id: matchId }, { selected: true });
        selectedFeatureIdRef.current = matchId;
      } catch {}
    }
  }, [map, selectedRecommendation, getPlaceCoords, getPlaceName, isSamePlace]);

  const resetMarkersChat = () => {
    // Remove any old DOM markers (legacy)
    markersChat.forEach((m) => m.remove());
    setMarkersChat([]);

    // Clear clustered source data
    featureCollectionRef.current = { type: "FeatureCollection", features: [] };
    placeIndexRef.current = {};
    // Guard against calling map methods on a removed instance (style becomes undefined after remove())
    try {
      if (map && (map as any).style) {
        const src = map.getSource(clusterSourceId) as GeoJSONSource | undefined;
        if (src) src.setData(featureCollectionRef.current as any);
      }
    } catch {
      // swallow – map may have been removed
    }
    // also stop any active orbit when resetting markers
    stopOrbit();
    selectedFeatureIdRef.current = null;
  };

  const flyMapTo = useCallback(
    (
      longitude: number,
      latitude: number,
      zoom: number = 12,
      is3D: boolean = false
    ) => {
      // stop any ongoing orbit before moving
      stopOrbit();
      // map?.flyTo({ center: [longitude, latitude] });

      let offset = [0, 0] as PointLike;
      // if (isMobile) offset = [0, -300];
      console.log("flyMapTo", longitude, latitude, zoom, is3D, offset);
      map?.flyTo({
        center: [longitude, latitude],
        zoom,
        essential: true,
        offset,
        pitch: is3D ? 70 : 0,
        bearing: is3D ? map.getBearing() : 0,
        ...(isMobile && { padding: { bottom: 300 } }),
      });

      // Removed expand/broadcast code
    },
    [map, stopOrbit, isMobile]
  );

  const getMarkersChat = () => {
    return markersChat;
  };

  const set3D = useCallback(
    (val: boolean, move: boolean = true) => {
      !val && stopOrbit();
      setIs3D(val);
      if (!map || !move) return;
      map.easeTo({
        ...(selectedRecommendation &&
        (("location" in selectedRecommendation &&
          selectedRecommendation.location.coordinates) ||
          ("experience" in selectedRecommendation &&
            selectedRecommendation.experience.coordinates))
          ? {
              center:
                "location" in selectedRecommendation &&
                selectedRecommendation.location.coordinates
                  ? [
                      selectedRecommendation.location.coordinates.longitude,
                      selectedRecommendation.location.coordinates.latitude,
                    ]
                  : "experience" in selectedRecommendation &&
                    selectedRecommendation.experience.coordinates
                  ? [
                      selectedRecommendation.experience.coordinates.longitude,
                      selectedRecommendation.experience.coordinates.latitude,
                    ]
                  : [0, 0],
            }
          : {}),
        zoom: val ? 16 : 12,
        pitch: val ? 70 : 0,
        bearing: val ? map.getBearing() : 0,
        duration: 600,
        essential: true,
        ...(isMobile && { padding: { bottom: 300 } }),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [map, selectedRecommendation, isMobile]
  );

  const openDetails = useCallback(
    (place: RecommendationData, interrupt: boolean) => {
      console.log("openDetails", place);
      setCurrentTile(place);
      setSelectedRecommendation(place);
      // enable 3D state (flyMapTo below will handle pitch)
      setIs3D(true);
      // Fly to POI coordinates
      if (
        "location" in place &&
        place.location.coordinates &&
        place.location.coordinates.longitude &&
        place.location.coordinates.latitude
      ) {
        flyMapTo(
          place.location.coordinates.longitude,
          place.location.coordinates.latitude,
          16,
          true
        );
        interrupt && agent?.sendEvent("changePlace", place.location);
        startOrbit(
          place.location.coordinates.longitude,
          place.location.coordinates.latitude
        );
      } else if (
        "experience" in place &&
        place.experience.coordinates &&
        place.experience.coordinates.longitude &&
        place.experience.coordinates.latitude
      ) {
        flyMapTo(
          place.experience.coordinates.longitude,
          place.experience.coordinates.latitude,
          16,
          true
        );
        interrupt && agent?.sendEvent("changePlace", place.experience);
        startOrbit(
          place.experience.coordinates.longitude,
          place.experience.coordinates.latitude
        );
      }
    },
    [agent, flyMapTo, startOrbit]
  );

  // Keep a live reference to openDetails for mapbox event handlers
  useEffect(() => {
    openDetailsRef.current = (p: RecommendationData, interrupt: boolean) => {
      openDetails(p, interrupt);
    };
  }, [openDetails]);

  const closeDetails = () => {
    setSelectedRecommendation(null);
    set3D(false, true);
    stopOrbit();
    // Clear selected visual state
    if (map && selectedFeatureIdRef.current !== null) {
      map.setFeatureState(
        { source: clusterSourceId, id: selectedFeatureIdRef.current },
        { selected: false }
      );
      selectedFeatureIdRef.current = null;
    }
    // Do not forcibly recenter; just close the dialog and stop orbit
    // collapse any marker card, if any consumers listen
    // Removed collapse/broadcast code
  };

  return (
    <MapsContext.Provider
      value={{
        agent,
        setAgent,
        map,
        markersChat,
        setMap,
        flyMapTo,
        setMarkersChat,
        addMarkerChat,
        resetMarkersChat,
        getMarkersChat,
        is3D,
        set3D,
        selectedRecommendation,
        setSelectedRecommendation,
        openDetails,
        closeDetails,
        currentTile,
        setCurrentTile,
      }}
    >
      {children}
    </MapsContext.Provider>
  );
};

export const useMaps = () => {
  const context = useContext(MapsContext);
  if (context === undefined) {
    throw new Error("useMaps must be used within a MapsProvider");
  }
  return context;
};
